import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  Avatar,
  Button,
  Divider,
  useMediaQuery,
  Theme,
  ListItemAvatar,
  ListItemText,
  Link,
} from '@mui/material';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import Image from 'next/image';
import {
  acceptCollaborateNotification,
  approveMemory,
  deleteNotification,
  inviteAccepted,
  removeMemory,
  setGuest,
} from '@/store/actions';
import { cdn_url, GetInitials } from '@/utils';
import { link } from 'fs';
interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
  listItem: any;
}

const DrawerComponent: React.FC<NotificationDrawerProps> = ({ open, onClose, listItem }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((template: Theme) => template.breakpoints.down('md'));
  const handleAccept = ({ actions, item }: any) => {
    if (actions?.type === 'COLLABORATION') {
      dispatch(setGuest(actions?.role));
      dispatch(inviteAccepted({ story_id: actions?.story_id, role_name: actions?.role }));
      dispatch(acceptCollaborateNotification(item));
      dispatch(deleteNotification(item.id));
      router.push(`/app/story/${actions?.story_id}`);
    }
    if (actions?.memory_id && actions?.story_id && actions?.type === 'APPROVAL') {
      dispatch(approveMemory({ id: actions?.memory_id, story_id: actions?.story_id }));
      dispatch(deleteNotification(item.id));
      router.push(`/app/story/${actions?.story_url}`);
      return;
    }
    if (actions?.type === 'APPROVED') {
      dispatch(deleteNotification(item.id));
      router.push(`/app/story/${actions?.story_url}`);
      return;
    }
    if (actions?.type === 'COLLABORATION_ACCEPTED') {
      dispatch(deleteNotification(item.id));

      return;
    }
  };
  const handleViewMemory = ({ actions, item }: any) => {
    router.push(`/app/story/${actions?.story_url}?memoryId=${actions?.memory_id}`);
    setTimeout(() => {
      dispatch(deleteNotification(item?.items[0]?.id));
    }, 400);
    onClose();
  };

  const handleApproveAll = () => {
    if (listItem && listItem.length > 0) {
      listItem.forEach((item: any) => {
        handleAccept({ actions: item.actions, item });
      });
    }
  };

  const handleDeleteNotification = (notificationId: string, actions: any) => {
    const { memory_id, story_id } = actions;
    dispatch(deleteNotification(notificationId));
    if (actions?.type === 'APPROVAL' && memory_id && story_id) {
      dispatch(removeMemory({ id: memory_id, story_id }));
    }
  };
  const groupedNotifications = listItem.reduce((acc: any, curr: any) => {
    const colabId = curr.colab_id;
    const type = curr.actions?.type;

    if (!colabId || !type) return acc;

    const groupKey = `${colabId}_${type}`;

    if (!acc[groupKey]) {
      acc[groupKey] = {
        colabId,
        type,
        actions: curr.actions,
        stories: [],
        items: [],
      };
    }

    if (curr.actions?.story_url) {
      acc[groupKey].stories.push(curr.actions.story_url);
    }

    acc[groupKey].items.push(curr);

    return acc;
  }, {});

  const groupedList = Object.values(groupedNotifications);
  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: !isMobile ? 400 : '100%',
          backgroundColor: '#212d6d',
          color: 'white',
          padding: 2,
        },
      }}>
      {/* Header */}
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        {/* Close Icon */}
        {isMobile ? (
          <Image
            priority
            src='/icons/left-arrow.svg'
            alt='close'
            style={{ cursor: 'pointer' }}
            width={20}
            height={20}
            onClick={onClose}
          />
        ) : (
          <Image
            src='/icons/close.svg'
            alt='close'
            style={{ cursor: 'pointer', display: isMobile ? 'none' : 'block' }}
            width={10}
            height={10}
            onClick={onClose}
          />
        )}

        {/* Centered Notifications Title */}
        <Typography
          variant='h1'
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 400,
            fontSize: '24px',
            lineHeight: '32px',
            flexGrow: 1,
            textAlign: 'center',
          }}>
          Notifications
        </Typography>

        {/* Approve All (Right) */}
        <Typography
          variant='body2'
          sx={{ cursor: 'pointer', color: '#fff', minWidth: 'auto' }}
          onClick={handleApproveAll}>
          Approve all
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'white', opacity: 0.2 }} />

      {/* Notification List */}
      <List>
        {groupedList.length > 0 ? (
          groupedList.map((item: any, index: number) => (
            <Box key={`${item.name}_${item.actions?.type}`}>
              <ListItem alignItems='flex-start' sx={{ flexDirection: 'column', alignItems: 'start' }}>
                <Box display='flex' alignItems='flex-start' width='100%' mb={1}>
                  {/* Avatar */}
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        backgroundColor: '#FFC1A1',
                        color: '#212d6d',
                        transform: 'translateY(-4px)',
                      }}
                      src={item.items[0].avatar ? `${cdn_url}${item.items[0].avatar}` : undefined}>
                      {!item.items[0].avatar &&
                        GetInitials(
                          item?.items[0]?.name?.split(' ')[0] || item?.items[0]?.name,
                          item?.items[0]?.name?.split(' ')[1] || '',
                        )}
                    </Avatar>
                  </ListItemAvatar>

                  {/* Name, Message & Story URLs */}
                  <Box flex={1}>
                    <ListItemText
                      primary={item?.actions?.type !== 'APPROVED' ? item.items[0]?.name : null}
                      secondary={
                        <Typography sx={{ color: '#B0B3C0' }}>
                          {item?.actions?.type === 'COLLABORATION_ACCEPTED'
                            ? item.items[0]?.message
                            : item.items[0]?.title}
                        </Typography>
                      }
                    />
                    {/* List of Story URLs with buttons */}
                    <Box display='flex' flexDirection='column' gap={1}>
                      {item?.actions?.type === 'APPROVED' &&
                      <Link
                        // href={`/app/story/${item?.actions?.story_url}`}
                         onClick={() => handleViewMemory({ actions: item.actions, item })}
                        underline='always'
                        sx={{cursor:'pointer', fontWeight: 500, color: 'white', flex: 1 }}
                        variant='body2'>
                        {item?.actions?.story_url}
                      </Link>
                      }
                      {item?.actions?.type === 'APPROVED'
                        ? item.items.map((itm: any, i: number) => (
                            <Box key={i} display='flex' justifyContent='space-between' alignItems='center'>
                              <Typography variant='body2' sx={{ fontWeight: 500, color: 'white', flex: 1 }}>
                                {itm?.message}
                              </Typography>

                              <Button
                                size='small'
                                variant='contained'
                                disabled
                                sx={{
                                  backgroundColor: '#fff',
                                  color: 'black',
                                  borderRadius: '20px',
                                  textTransform: 'none',
                                  '&:hover': { backgroundColor: 'white' },
                                  gap: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  '&.Mui-disabled': {
                                    backgroundColor: '#fff',
                                    color: 'black',
                                  },
                                }}>
                                <Image src='/icons/tick-black.svg' alt='tick' width={15} height={15} />
                                Approved!
                              </Button>
                            </Box>
                          ))
                        : item.stories.map((story: string, i: number) => (
                            <Box key={i} display='flex' justifyContent='space-between' alignItems='center'>
                              <Typography variant='body2' sx={{ fontWeight: 500, color: 'white', flex: 1 }}>
                                {story}
                              </Typography>

                              {item?.actions?.type !== 'COLLABORATION_ACCEPTED' && (
                                <Button
                                  size='small'
                                  variant='contained'
                                  sx={{
                                    backgroundColor: '#0072CE',
                                    color: 'white',
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    '&:hover': { backgroundColor: '#005FA3' },
                                  }}
                                  onClick={() => handleViewMemory({ actions: item.actions, item })}>
                                  View Memory
                                </Button>
                              )}
                            </Box>
                          ))}
                    </Box>
                  </Box>
                </Box>
              </ListItem>

              {/* Divider */}
              {index < groupedList.length - 1 && <Divider sx={{ borderColor: 'white', opacity: 0.2 }} />}
            </Box>
          ))
        ) : (
          <Typography sx={{ textAlign: 'center', color: '#B0B3C0', mt: 2 }}>No notifications</Typography>
        )}
      </List>
    </Drawer>
  );
};

export default DrawerComponent;
